import { ProductsPicker } from '@wix/bookings-products-picker';
import '@wix/bookings-products-picker/styles.global.css';
import { useSettings } from '@wix/tpa-settings/react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import React, { FC, useState } from 'react';
import settingsParams from '../../settingsParams';
import { getContent } from '../../../../utils/content/content';
import type { SelectedProduct, V3Product } from '../../../../utils/state/types';
import { classes } from './RelatedProducts.st.css';

interface RelatedProductsProps {
  products: V3Product[];
  selection: SelectedProduct[];
  onSelectionChange: (next: SelectedProduct[]) => void;
  onModalOpen: (productId: string) => void;
  isLoadingProductDetails: boolean;
}

export const RelatedProducts: FC<RelatedProductsProps> = ({
  products,
  selection,
  onSelectionChange,
  onModalOpen,
  isLoadingProductDetails,
}) => {
  // Bump on every modal-close event so the picker (and all @wix/headless-stores
  // services nested under it) gets a fresh mount before the next open.
  // Without this, @preact/signals-react/runtime's useSignals subscription stays
  // stale across the picker's internal modal remount and option-chip / quantity /
  // gallery clicks update the service signals but never re-render the modal.
  const [pickerKey, setPickerKey] = useState(0);
  const bumpPickerKey = () => setPickerKey((n) => n + 1);
  const handleSelectionChange = (next: SelectedProduct[]) => {
    onSelectionChange(next);
    bumpPickerKey();
  };
  const handleModalDismiss = () => {
    bumpPickerKey();
  };
  const { t } = useTranslation();
  const settings = useSettings();
  const title = getContent({
    settings,
    settingsParam: settingsParams.relatedProductsTitle,
  });
  const subtitle = getContent({
    settings,
    settingsParam: settingsParams.relatedProductsSubtitle,
  });
  const ctaLabel = getContent({
    settings,
    settingsParam: settingsParams.relatedProductsAddToBookingLabel,
  });
  const oosLabel = getContent({
    settings,
    settingsParam: settingsParams.relatedProductsOutOfStockLabel,
  });
  const showOutOfStock = settings.get(settingsParams.showOutOfStockProducts);
  const maxItems = settings.get(settingsParams.maxRelatedProductsToDisplay);

  if (!products.length) {
    return null;
  }

  return (
    <div className={classes.root}>
      <h3 className={classes.title}>{title}</h3>
      <p className={classes.subtitle}>{subtitle}</p>
      <ProductsPicker
        key={pickerKey}
        className={classes.pickerRoot}
        products={products.slice(0, maxItems)}
        selection={selection}
        onSelectionChange={handleSelectionChange}
        hideOutOfStock={!showOutOfStock}
        onModalOpen={onModalOpen}
        onModalDismiss={handleModalDismiss}
        isLoadingProductDetails={isLoadingProductDetails}
        labels={{
          addButtonLabel: t('app.bookings-form.related-products.add-button'),
          editButtonLabel: t('app.bookings-form.related-products.edit-button'),
          deleteAriaLabel: t(
            'app.bookings-form.related-products.remove-button.aria-label',
          ),
          outOfStockLabel: oosLabel,
          modalTitle: t('app.bookings-form.related-products.modal.title'),
          closeAriaLabel: t('app.bookings-form.close-modal.aria-label'),
          addToBookingLabel: (price) =>
            price ? `${ctaLabel} - ${price}` : ctaLabel,
        }}
      />
    </div>
  );
};
